"use client"
import { useState } from "react"
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
} from "@mui/material"
import { Add, Edit, Visibility, Print, CheckCircle, Science } from "@mui/icons-material"
import { useApp } from "../contexts/AppContext"

export default function LabManagement({ user }) {
  const { state, actions } = useApp()
  const [open, setOpen] = useState(false)
  const [resultOpen, setResultOpen] = useState(false)
  const [selectedTest, setSelectedTest] = useState(null)
  const [formData, setFormData] = useState({
    patientName: "",
    patientId: "",
    testName: "",
    category: "",
    doctorName: "",
    instructions: "",
  })
  const [resultData, setResultData] = useState({})

  const testCategories = ["Hematology", "Biochemistry", "Microbiology", "Pathology", "Radiology"]
  const commonTests = [
    "Complete Blood Count",
    "Lipid Profile",
    "Liver Function Test",
    "Kidney Function Test",
    "Thyroid Function Test",
    "Blood Sugar",
    "Urine Analysis",
    "X-Ray Chest",
    "ECG",
    "Ultrasound",
  ]

  const handleOpen = (test = null) => {
    if (test) {
      setSelectedTest(test)
      setFormData({
        patientName: test.patientName,
        patientId: test.patientId,
        testName: test.testName,
        category: test.category,
        doctorName: test.doctorName,
        instructions: test.instructions || "",
      })
    } else {
      setSelectedTest(null)
      setFormData({
        patientName: "",
        patientId: "",
        testName: "",
        category: "",
        doctorName: "",
        instructions: "",
      })
    }
    setOpen(true)
  }

  const handleResultOpen = (test) => {
    setSelectedTest(test)
    setResultData(test.results || {})
    setResultOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setResultOpen(false)
    setSelectedTest(null)
  }

  const handleSave = () => {
    if (selectedTest) {
      actions.updateLabTest({ ...selectedTest, ...formData })
    } else {
      actions.addLabTest({
        ...formData,
        testId: `T${(state.labTests.length + 1).toString().padStart(4, "0")}`,
        date: new Date().toISOString().split("T")[0],
        status: "Pending",
        results: {},
        normalRanges: {},
      })
    }
    handleClose()
  }

  const handleResultSave = () => {
    actions.updateLabTest({
      ...selectedTest,
      results: resultData,
      status: "Completed",
      completedDate: new Date().toISOString().split("T")[0],
    })
    handleClose()
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "success"
      case "Pending":
        return "warning"
      case "In Progress":
        return "info"
      default:
        return "default"
    }
  }

  const pendingTests = state.labTests.filter((t) => t.status === "Pending").length
  const completedTests = state.labTests.filter((t) => t.status === "Completed").length
  const totalTests = state.labTests.length

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Laboratory Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
          Add New Test
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Science sx={{ fontSize: 40, color: "primary.main", mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Tests
                  </Typography>
                  <Typography variant="h4">{totalTests}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Tests
              </Typography>
              <Typography variant="h4" color="warning.main">
                {pendingTests}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed Tests
              </Typography>
              <Typography variant="h4" color="success.main">
                {completedTests}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Test ID</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Test Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Doctor</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.labTests.map((test) => (
              <TableRow key={test.id}>
                <TableCell>{test.testId}</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{test.patientName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {test.patientId}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{test.testName}</TableCell>
                <TableCell>{test.category}</TableCell>
                <TableCell>{test.doctorName}</TableCell>
                <TableCell>{test.date}</TableCell>
                <TableCell>
                  <Chip label={test.status} color={getStatusColor(test.status)} size="small" />
                </TableCell>
                <TableCell>
                  <Tooltip title="View Results">
                    <IconButton size="small">
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print Report">
                    <IconButton size="small">
                      <Print />
                    </IconButton>
                  </Tooltip>
                  {test.status === "Pending" && (
                    <Tooltip title="Add Results">
                      <IconButton size="small" onClick={() => handleResultOpen(test)}>
                        <CheckCircle />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpen(test)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Test Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{selectedTest ? "Edit Test" : "Add New Test"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Patient Name"
                value={formData.patientName}
                onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Patient ID"
                value={formData.patientId}
                onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Test Name"
                value={formData.testName}
                onChange={(e) => setFormData({ ...formData, testName: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select Test</option>
                {commonTests.map((test) => (
                  <option key={test} value={test}>
                    {test}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select Category</option>
                {testCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Doctor Name"
                value={formData.doctorName}
                onChange={(e) => setFormData({ ...formData, doctorName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Special Instructions"
                multiline
                rows={3}
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {selectedTest ? "Update" : "Add"} Test
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Results Dialog */}
      <Dialog open={resultOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Add Test Results - {selectedTest?.testName}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Patient: {selectedTest?.patientName} ({selectedTest?.patientId})
              </Typography>
            </Grid>

            {/* Dynamic result fields based on test type */}
            {selectedTest?.testName === "Complete Blood Count" && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hemoglobin (g/dL)"
                    value={resultData.hemoglobin || ""}
                    onChange={(e) => setResultData({ ...resultData, hemoglobin: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="WBC Count (/μL)"
                    value={resultData.wbc || ""}
                    onChange={(e) => setResultData({ ...resultData, wbc: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Platelet Count (/μL)"
                    value={resultData.platelets || ""}
                    onChange={(e) => setResultData({ ...resultData, platelets: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="RBC Count (million/μL)"
                    value={resultData.rbc || ""}
                    onChange={(e) => setResultData({ ...resultData, rbc: e.target.value })}
                  />
                </Grid>
              </>
            )}

            {selectedTest?.testName === "Lipid Profile" && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Total Cholesterol (mg/dL)"
                    value={resultData.totalCholesterol || ""}
                    onChange={(e) => setResultData({ ...resultData, totalCholesterol: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="HDL Cholesterol (mg/dL)"
                    value={resultData.hdl || ""}
                    onChange={(e) => setResultData({ ...resultData, hdl: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="LDL Cholesterol (mg/dL)"
                    value={resultData.ldl || ""}
                    onChange={(e) => setResultData({ ...resultData, ldl: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Triglycerides (mg/dL)"
                    value={resultData.triglycerides || ""}
                    onChange={(e) => setResultData({ ...resultData, triglycerides: e.target.value })}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={3}
                value={resultData.notes || ""}
                onChange={(e) => setResultData({ ...resultData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleResultSave} variant="contained">
            Save Results
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
